import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaGlobe, FaChevronDown, FaVolumeUp } from "react-icons/fa";
import { SUPPORTED_LANGUAGES, getLanguageNativeName, getLanguageName } from "../../utils/audioTranslation";
import "./LanguageDropdown.css";

const LanguageDropdown = ({
    selectedLanguage,
    onLanguageChange,
    isTranslating,
    onToggleTranslation,
    currentTranslation
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [translationDisplayPosition, setTranslationDisplayPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const toggleButtonRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside the dropdown container and outside the portal dropdown
            const dropdownElement = document.querySelector('.language-dropdown');
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                if (dropdownElement && !dropdownElement.contains(event.target)) {
                    setIsOpen(false);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Filter languages based on search
    const filteredLanguages = SUPPORTED_LANGUAGES.filter(lang =>
        lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lang.nativeName.includes(searchTerm) ||
        lang.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedLang = SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage);

    // Handle button click to calculate position for fixed dropdown
    const handleButtonClick = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                left: rect.right - 280 // Align right edge with button
            });
        }
        setIsOpen(!isOpen);
    };

    // Dropdown content
    const dropdownContent = isOpen && (
        <div className="language-dropdown" style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, zIndex: 10001 }}>
            {/* Search Input */}
            <div className="language-search">
                <input
                    type="text"
                    placeholder="Search language..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="language-search-input"
                />
            </div>

            {/* Language List */}
            <div className="language-list">
                {filteredLanguages.map((lang) => (
                    <div
                        key={lang.code}
                        className={`language-option ${selectedLanguage === lang.code ? 'selected' : ''}`}
                        onClick={() => {
                            onLanguageChange(lang.code);
                            setIsOpen(false);
                            setSearchTerm("");
                        }}
                    >
                        <span className="lang-native">{lang.nativeName}</span>
                        <span className="lang-name">{lang.name}</span>
                        {selectedLanguage === lang.code && (
                            <span className="lang-check">✓</span>
                        )}
                    </div>
                ))}
                {filteredLanguages.length === 0 && (
                    <div className="no-results">No languages found</div>
                )}
            </div>
        </div>
    );

    return (
        <div className="language-dropdown-container" ref={dropdownRef}>
            {/* Language Selector */}
            <div className="language-selector">
                <button
                    ref={buttonRef}
                    className="language-button"
                    onClick={handleButtonClick}
                    title="Select translation language"
                >
                    <FaGlobe className="globe-icon" />
                    <span className="selected-language">
                        {selectedLang ? selectedLang.nativeName : 'Select'}
                    </span>
                    <FaChevronDown className={`chevron-icon ${isOpen ? 'open' : ''}`} />
                </button>
            </div>

            {/* Render dropdown using portal to escape header overflow */}
            {createPortal(dropdownContent, document.body)}

            {/* Translation Toggle Button */}
            <button
                ref={toggleButtonRef}
                className={`translation-toggle ${isTranslating ? 'active' : ''}`}
                onClick={() => {
                    // Calculate position for translation display before toggling
                    if (!isTranslating && toggleButtonRef.current) {
                        const rect = toggleButtonRef.current.getBoundingClientRect();
                        setTranslationDisplayPosition({
                            top: rect.bottom + 8,
                            left: rect.right - 350
                        });
                    }
                    onToggleTranslation();
                }}
                title={isTranslating ? "Stop Translation" : "Start Translation"}
                disabled={!selectedLanguage}
            >
                <FaVolumeUp className={`translate-icon ${isTranslating ? 'pulsing' : ''}`} />
                <span className="translate-label">
                    {isTranslating ? 'Stop' : 'Translate'}
                </span>
            </button>

            {/* Current Translation Display - using portal to escape header */}
            {isTranslating && currentTranslation && createPortal(
                <div
                    className="translation-display"
                    style={{
                        position: 'fixed',
                        top: translationDisplayPosition.top,
                        left: translationDisplayPosition.left
                    }}
                >
                    <div className="translation-original">
                        <span className="label">Original:</span>
                        <span className="text">{currentTranslation.original}</span>
                    </div>
                    {currentTranslation.translated && (
                        <div className="translation-result">
                            <span className="label">{getLanguageName(selectedLanguage)}:</span>
                            <span className="text">{currentTranslation.translated}</span>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default LanguageDropdown;
